import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';

import { Button } from '@rocco/components/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@rocco/components/dialog';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerSwipeZone,
    DrawerTitle,
} from '@rocco/components/drawer';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-left.svg';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-right.svg';

import { EnquiryForm } from '../component/EnquiryForm';
import { SelectedItemsList } from './SelectedItemsList';
import { SelectedItemsThumbnails } from './SelectedItemsThumbnails';
import {
    fanOutNote,
    messageSubject,
    selectionSummary,
} from './bulk-enquiry-copy';
import {
    useBulkEnquiryMode,
    useBulkEnquirySelection,
} from './bulk-enquiry-state';

interface BulkEnquiryModalProps {
    open: boolean;
    /** Reopen straight onto the form, rather than back at the review step. */
    startAtForm?: boolean;
    onClose: () => void;
    /**
     * The user backed out of the login prompt that submitting raised. The form
     * closed this modal to make way for it, so bring it back rather than making
     * them start again from the island.
     */
    onAuthCancel: () => void;
}

/** Review the selection first, then fill in the enquiry. */
type Step = 'review' | 'form';

/**
 * The generic multi-supplier bulk enquiry modal, in two steps: first review (and
 * prune) the selected items, then fill in the enquiry form. It carries no
 * per-professional branding and reuses {@link EnquiryForm} for the contact
 * fields, validation, auth and tracking — submitting the selection as
 * `SiteTreeIDs[]` / `TargetProfessionals[]`, which the backend fans out to each
 * item's professional.
 *
 * Reads the live selection so removals here reflect everywhere. The selection
 * stays intact through the success screen and is cleared (mode off) only once
 * the modal closes after a successful submit; dismissing beforehand keeps it.
 */
export const BulkEnquiryModal = ({
    open,
    startAtForm = false,
    onClose,
    onAuthCancel,
}: BulkEnquiryModalProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const { items, supplierCount, remove, clear } = useBulkEnquirySelection();
    const { exit } = useBulkEnquiryMode();
    const [submitted, setSubmitted] = useState(false);
    const [step, setStep] = useState<Step>('review');

    // Each time the modal (re)closes, start the next one back at the review step —
    // unless it's being brought back after a cancelled login, where they were
    // already at the form.
    useEffect(() => {
        if (!open) {
            setSubmitted(false);
            setStep('review');
        } else if (startAtForm) {
            setStep('form');
        }
    }, [open, startAtForm]);

    // Emptying the selection before submitting closes the modal.
    useEffect(() => {
        if (open && !submitted && items.length === 0) onClose();
    }, [open, submitted, items.length, onClose]);

    const itemType = useMemo(() => {
        const first = items[0]?.itemType ?? 'product';
        return items.every((item) => item.itemType === first)
            ? first
            : 'product';
    }, [items]);

    // The form is shaped around a single professional, but a bulk enquiry names
    // no targets at all — the backend splits the items by owner and sends each
    // supplier only their own. So this synthetic professional exists purely to
    // satisfy the form (tracking label, session key); it never routes anything.
    const professional = useMemo(
        () => ({
            ID: items[0]?.targetProfessionalId ?? 0,
            Title:
                supplierCount > 1 ? 'the selected suppliers' : 'the supplier',
            branchOptions: [],
        }),
        [items, supplierCount],
    );

    const siteTreeIDs = useMemo(
        () => items.map((item) => item.siteTreeID),
        [items],
    );

    // Each professional receives their own enquiry carrying only their own
    // things, so the shared message must never quote the whole list's count.
    const itemTitle = messageSubject(items, supplierCount);

    const handleClose = useCallback(() => {
        // Clear the selection (and leave bulk mode) only after a successful
        // submit; a plain dismiss keeps it for later.
        if (submitted) exit();
        onClose();
    }, [submitted, exit, onClose]);

    const handleSubmitted = useCallback(() => setSubmitted(true), []);

    // The title names the step you're on: reviewing the list, then sending it.
    const title = step === 'review' ? 'Your enquiry list' : 'Send your enquiry';
    const summary = selectionSummary(items, supplierCount);
    const note = fanOutNote(items, supplierCount);

    const reviewStep = (
        <>
            {note ? <p className="mb-3 text-sm text-gray-500">{note}</p> : null}
            <SelectedItemsList
                items={items}
                onRemove={remove}
                onClearAll={clear}
                groupBySupplier
                className="border-y border-gray-200 py-1"
            />
            <div className="mt-6 flex justify-end">
                <Button
                    color="primary"
                    size="larger"
                    onClick={() => setStep('form')}
                >
                    Continue to enquiry
                    <ArrowRight className="ml-2 size-4 fill-current" />
                </Button>
            </div>
        </>
    );

    const formStep = (
        <>
            {!submitted ? (
                <button
                    type="button"
                    onClick={() => setStep('review')}
                    className="mb-4 flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900"
                >
                    <ArrowLeft className="size-3.5 fill-current" />
                    Back to list
                </button>
            ) : null}
            <EnquiryForm
                professional={professional}
                itemType={itemType}
                siteTreeID={items[0]?.siteTreeID ?? 0}
                siteTreeIDs={siteTreeIDs}
                source="EnquiryModal"
                includeBranch={false}
                showCustomEnquiryMessage={false}
                isModal
                showSuccessMessage
                itemTitle={itemTitle}
                ctaLabel="Enquire"
                onSuccess={handleSubmitted}
                onFailure={handleClose}
                onAuthCancel={onAuthCancel}
                onSuccessClose={handleClose}
            />
        </>
    );

    const body = step === 'review' ? reviewStep : formStep;

    if (isMobile) {
        return (
            <Drawer
                open={open && items.length > 0}
                repositionInputs={false}
                onOpenChange={(next) => {
                    if (!next) handleClose();
                }}
            >
                {/* h-auto lets the sheet hug the review list (CTA lands in the
                    thumb zone instead of floating over a void); the taller form
                    step still grows to the same cap. */}
                <DrawerContent
                    className="h-auto max-h-[calc(100dvh-0.5rem)] bg-gray-50"
                    variant="default"
                    hideDefaultCloseButton
                    noHandle
                    scrollable
                    leadingContent={<DrawerSwipeZone onDismiss={handleClose} />}
                >
                    <DrawerHeader className="mb-3 text-left">
                        <div className="flex items-center gap-x-4">
                            {/* The review step shows the full list already; on the
                            form step this stack is the only reminder of what is
                            being enquired about. */}
                            {step === 'form' ? (
                                <SelectedItemsThumbnails items={items} />
                            ) : null}
                            <div className="min-w-0 grow">
                                <DrawerTitle>{title}</DrawerTitle>
                                <p className="mt-1 text-sm text-gray-500">
                                    {summary}
                                </p>
                            </div>
                        </div>
                        <DrawerDescription className="hidden" aria-hidden />
                    </DrawerHeader>
                    <div className="px-4 pb-8">{body}</div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog
            open={open && items.length > 0}
            onOpenChange={(next) => {
                if (!next) handleClose();
            }}
        >
            <DialogContent className="max-h-[95vh] w-[50rem] max-w-[95vw] overflow-y-auto bg-gray-50 p-4 md:p-12">
                <DialogHeader className="text-left md:mb-6">
                    <div className="flex items-center gap-x-6">
                        {/* The review step shows the full list already; on the form
                        step this stack is the only reminder of what is being
                        enquired about. */}
                        {step === 'form' ? (
                            <SelectedItemsThumbnails items={items} />
                        ) : null}
                        <div className="min-w-0 grow">
                            <DialogTitle>{title}</DialogTitle>
                            <p className="mt-1 text-sm text-gray-500">
                                {summary}
                            </p>
                        </div>
                    </div>
                    <DialogDescription className="hidden" aria-hidden />
                </DialogHeader>
                {body}
            </DialogContent>
        </Dialog>
    );
};
